import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { FormGrid } from "@/components/ui/form-grid";
import { FormLabel } from "@/components/ui/form-label";
import { InputWrapper } from "@/components/ui/input-wrapper";
import { Text } from "@/components/ui/text";
import { VStack } from "@/components/ui/v-stack";
import {
  getCurrencies,
  createCurrency,
  updateCurrency,
  type Currency,
} from "@/lib/api/currencies";
import {
  LocalizedNamesFields,
  toLocalizedName,
  type LocalizedNamesDraft,
} from "@/components/admin/catalogs/localized-names";
import { CatalogPageTemplate } from "@/components/admin/catalog-page-template";

type CurrencyRow = Currency & { is_deleted?: boolean };

function AdminCurrenciesCatalogPage() {
  const { t } = useTranslation("admin.catalogs.currencies");

  return (
    <CatalogPageTemplate<CurrencyRow>
      title={t("title")}
      tableId="catalog-currencies"
      queryKey={["admin", "catalogs", "currencies"]}
      fetchFn={getCurrencies}
      columns={[
        {
          key: "name",
          header: t("columns.nameEn"),
          defaultWidth: 200,
          minWidth: 100,
          render: (item, isDeleted) => (
            <Text
              weight="medium"
              className={
                isDeleted
                  ? "text-[color:var(--on-surface-variant)] opacity-70"
                  : ""
              }
            >
              {item.names?.en ?? item.name}
            </Text>
          ),
        },
        {
          key: "code",
          header: t("columns.code"),
          defaultWidth: 120,
          minWidth: 80,
          render: (item, isDeleted) => (
            <Text
              className={
                isDeleted
                  ? "text-[color:var(--on-surface-variant)] opacity-70"
                  : ""
              }
            >
              {item.code}
            </Text>
          ),
        },
        {
          key: "symbol",
          header: t("columns.symbol"),
          defaultWidth: 100,
          minWidth: 60,
          render: (item, isDeleted) => (
            <Text
              className={
                isDeleted
                  ? "text-[color:var(--on-surface-variant)] opacity-70"
                  : ""
              }
            >
              {item.symbol ?? "—"}
            </Text>
          ),
        },
      ]}
      searchFilter={(item, q) =>
        (item.names?.en ?? item.name).toLowerCase().includes(q) ||
        (item.names?.ru ?? "").toLowerCase().includes(q) ||
        (item.names?.uz ?? "").toLowerCase().includes(q) ||
        item.code.toLowerCase().includes(q) ||
        (item.symbol ?? "").toLowerCase().includes(q)
      }
      searchPlaceholder={t("searchPlaceholder")}
      emptyMessage={t("noItems")}
      addLabel={t("add")}
      editTitle={t("modalEditTitle")}
      addTitle={t("modalAddTitle")}
      failedToLoadMessage={t("failedToLoad")}
      failedToSaveMessage={t("failedToSave")}
      deleteEndpoint="/currencies"
      editLabel={t("edit")}
      itemToFormData={(item) => ({
        namesDraft: {
          en: item.names?.en ?? item.name ?? "",
          ru: item.names?.ru ?? "",
          uz: item.names?.uz ?? "",
        } as LocalizedNamesDraft,
        code: item.code ?? "",
        symbol: item.symbol ?? "",
      })}
      emptyFormData={() => ({
        namesDraft: { en: "", ru: "", uz: "" } as LocalizedNamesDraft,
        code: "",
        symbol: "",
      })}
      formDataToPayload={(fd) => ({
        name: (fd.namesDraft as LocalizedNamesDraft).en,
        names: toLocalizedName(fd.namesDraft as LocalizedNamesDraft),
        code: (fd.code as string).trim().toUpperCase(),
        symbol: (fd.symbol as string).trim() || undefined,
      })}
      isFormValid={(fd) =>
        Boolean((fd.namesDraft as LocalizedNamesDraft).en.trim()) &&
        (fd.code as string).trim().length === 3
      }
      createFn={(payload) =>
        createCurrency(payload as Parameters<typeof createCurrency>[0])
      }
      updateFn={(id, payload) =>
        updateCurrency(id, payload as Parameters<typeof updateCurrency>[1])
      }
      renderForm={(formData, setFormData, submitting) => (
        <>
          <LocalizedNamesFields
            value={formData.namesDraft as LocalizedNamesDraft}
            onChange={(next) =>
              setFormData((prev) => ({ ...prev, namesDraft: next }))
            }
            disabled={submitting}
            testIdPrefix="admin-currency"
          />
          <FormGrid>
            <VStack gap="xs">
              <FormLabel>{t("fields.code")}</FormLabel>
              <InputWrapper
                value={formData.code as string}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, code: e.target.value }))
                }
                disabled={submitting}
                data-testid="admin-currency-code"
              />
            </VStack>
            <VStack gap="xs">
              <FormLabel>{t("fields.symbol")}</FormLabel>
              <InputWrapper
                value={formData.symbol as string}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, symbol: e.target.value }))
                }
                disabled={submitting}
                data-testid="admin-currency-symbol"
              />
            </VStack>
          </FormGrid>
        </>
      )}
      testIdPrefix="admin-catalog-currencies"
    />
  );
}

export const Route = createFileRoute("/_app/admin/catalogs/currencies")({
  component: AdminCurrenciesCatalogPage,
});
